import React, { useState, useEffect, memo, useCallback, useMemo } from 'react';
import { 
  DragDropContext, 
  Droppable, 
  Draggable,
  DropResult 
} from '@hello-pangea/dnd';
import { 
  Card, 
  CardContent, 
  CardHeader 
} from '@/components/ui/card';
import { 
  Plane, 
  Building, 
  Map, 
  ChevronRight, 
  ChevronDown,
  GripVertical, 
  Trash, 
  Box,
  CircleDot,
  Car,
  Utensils,
  Plus,
  BookOpen,
  LogIn,
  LogOut,
  Search,
  AlignLeft,
  MoreHorizontal,
  Route,
  Blocks,
  MoveRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from '@/lib/utils';
import { Label as UILabel } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { 
  FlightInfo, 
  AccommodationInfo, 
  TourInfo,
  TransportationInfo,
  RestaurantInfo,
  QuotationPage,
  QuotationBlock,
  FlightConnection,
  LibraryItem
} from './types';
import { QuotationBlock as QuotationBlockComponent } from './QuotationBlock';
import { AccommodationForm as AccommodationFormComponent } from './AccommodationForm';
import { AccommodationEventItem as AccommodationEventItemComponent } from './AccommodationEventItem';
import { TourForm } from './TourForm';
import { FlightForm } from './FlightForm';
import { TransportationForm } from './TransportationForm';
import { RestaurantForm } from './RestaurantForm';
import { format } from 'date-fns';
import {
  formatFlightNumber,
  validateFlightNumber,
  getAirlineName,
  calculateMinutesBetweenISOStrings,
  formatDuration
} from '@/lib/quotation-utils';
import { LibraryBrowser } from './LibraryBrowser';
import { useLibrary } from '@/hooks/useLibrary';
import { useToast } from '@/hooks/use-toast';
import { FlightEventItem } from './FlightEventItem';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { FlightEventForm } from './FlightEventForm';
import { AccommodationEventForm } from './AccommodationEventForm';
import { PageSelectionModal } from './PageSelectionModal';
import { globalMoveFlightEvent, globalMoveAccommodationToPage, globalMoveTransportationToPage } from '@/hooks/useQuotationForm';
import { globalMoveFlightToPage as globalMoveFlightToPageFunc } from '@/hooks/useQuotationForm';

// Unified item type
interface UnifiedItem {
  id: string;
  type: 'flight' | 'accommodation' | 'tour' | 'transportation' | 'restaurant' | 'block' | 'accommodation-checkin' | 'accommodation-checkout' | 'flight-departure' | 'flight-arrival' | 'flight-event' | 'accommodation-event';
  globalOrder: number;
  flight?: FlightInfo;
  accommodation?: AccommodationInfo;
  tour?: TourInfo;
  transportation?: TransportationInfo;
  restaurant?: RestaurantInfo;
  block?: QuotationBlock;
  accommodationId?: string;
  flightId?: string;
  flightEvent?: {
    title: string;
    flightId: string;
    isDeparture: boolean;
  };
  accommodationEvent?: {
    id: string;
    title: string;
    accommodationId: string;
    isCheckIn: boolean;
    globalOrder?: number;
  };
}

interface UnifiedItemsViewProps {
  page: QuotationPage;
  updatePage: (id: string, field: keyof QuotationPage, value: any) => void;
  addFlight: (pageId: string) => void;
  addAccommodation: (pageId: string) => void;
  addTour: (pageId: string) => void;
  addTransportation: (pageId: string) => void;
  addRestaurant: (pageId: string) => void;
  addBlock: (pageId: string) => void;
  addFlightEvent: (pageId: string) => void;
  addAccommodationEvent: (pageId: string) => void;
  removeItem: (itemId: string, itemType: string) => void;
  updateBlock: (pageId: string, blockId: string, field: keyof QuotationBlock, value: any) => void;
  deleteBlock: (pageId: string, blockId: string) => void;
  updateAccommodation: (pageId: string, accommodationId: string, field: keyof AccommodationInfo, value: any) => void;
  deleteAccommodation: (pageId: string, accommodationId: string) => void;
  moveTourToPage?: (sourcePageId: string, tourId: string, targetPageId: string) => void;
  moveRestaurantToPage?: (sourcePageId: string, restaurantId: string, targetPageId: string) => void;
  moveAccommodationToPage?: (sourcePageId: string, accommodationId: string, targetPageId: string) => void;
  moveFlightEventToPage?: (sourcePageId: string, flightEventId: string, targetPageId: string) => void;
  moveAccommodationEventToPage?: (sourcePageId: string, accommodationEventId: string, targetPageId: string) => void;
  moveTransportationToPage?: (sourcePageId: string, transportationId: string, targetPageId: string) => void;
  moveFlightToPage?: (sourcePageId: string, flightId: string, targetPageId: string) => void;
  moveBlockToPage?: (sourcePageId: string, blockId: string, targetPageId: string) => void;
  allPages?: any[];
}

// Adicionar nova interface para movimentação de transporte
interface TransportationContextMenuProps {
  pageId: string;
  transportationId: string;
  allPages: any[];
}

export function UnifiedItemsView({ 
  page, 
  updatePage,
  addFlight,
  addAccommodation,
  addTour,
  addTransportation,
  addRestaurant,
  addBlock,
  addFlightEvent,
  addAccommodationEvent,
  removeItem,
  updateBlock,
  deleteBlock,
  updateAccommodation,
  deleteAccommodation,
  moveTourToPage,
  moveRestaurantToPage,
  moveAccommodationToPage,
  moveFlightEventToPage,
  moveAccommodationEventToPage,
  moveTransportationToPage,
  moveFlightToPage,
  moveBlockToPage,
  allPages = []
}: UnifiedItemsViewProps) {
  // IMPORTANTE: Debug log para verificar estado das props recebidas
  console.log('[UnifiedItemsView] Recebendo props:', {
    pageId: page.id, 
    hasMoveAccommodationToPage: typeof moveAccommodationToPage === 'function',
    hasMoveRestaurantToPage: typeof moveRestaurantToPage === 'function',
    hasMoveFlightEventToPage: typeof moveFlightEventToPage === 'function', 
    hasMoveAccommodationEventToPage: typeof moveAccommodationEventToPage === 'function',
    hasMoveTransportationToPage: typeof moveTransportationToPage === 'function',
    hasMoveFlightToPage: typeof moveFlightToPage === 'function',
    hasMoveBlockToPage: typeof moveBlockToPage === 'function',
    allPagesCount: allPages?.length,
  });

  // Debug log para funções disponíveis e fallbacks
  useEffect(() => {
    console.log('[UnifiedItemsView] Functions availability check:', {
      hasMoveFlightEventFunction: Boolean(moveFlightEventToPage),
      hasMoveAccommodationEventFunction: Boolean(moveAccommodationEventToPage),
      hasMoveTransportationFunction: Boolean(moveTransportationToPage),
      hasFallbackFunction: true,
      hasGlobalFunctionFlightEvent: typeof globalMoveFlightEvent === 'function',
      hasGlobalFunctionAccommodationToPage: typeof globalMoveAccommodationToPage === 'function',
      hasGlobalFunctionTransportationToPage: typeof globalMoveTransportationToPage === 'function'
    });
  }, [moveFlightEventToPage, moveAccommodationEventToPage, moveTransportationToPage]);

  // Local state
  const [items, setItems] = useState<UnifiedItem[]>([]);
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  // Library state
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [currentItemType, setCurrentItemType] = useState<string>('all');
  const { toast } = useToast();
  const [selectedFlight, setSelectedFlight] = useState<FlightInfo | null>(null);
  const [isFlightModalOpen, setIsFlightModalOpen] = useState(false);
  const [selectedTourId, setSelectedTourId] = useState<string | null>(null);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string | null>(null);
  const [selectedAccommodationId, setSelectedAccommodationId] = useState<string | null>(null);
  const [selectedFlightId, setSelectedFlightId] = useState<string | null>(null);
  const [isPageSelectionOpen, setIsPageSelectionOpen] = useState(false);
  // Atualizar a definição do estado selectionType para incluir 'block'
  const [selectionType, setSelectionType] = useState<'tour' | 'restaurant' | 'accommodation' | 'flightEvent' | 'accommodationEvent' | 'transportation' | 'flight' | 'block' | null>(null);
  const [selectedFlightEventId, setSelectedFlightEventId] = useState<string | null>(null);
  const [selectedAccommodationEventId, setSelectedAccommodationEventId] = useState<string | null>(null);
  const [selectedTransportationId, setSelectedTransportationId] = useState<string | null>(null);
  // Remover a declaração duplicada de selectedBlockId
  // const [selectedBlockId, setSelectedBlockId] = useState<string>('');

  // Calculate all flights from all pages, including page info
  const allFlights = useMemo(() => {
    return (allPages || []).reduce((acc, currentPage) => {
      // Adicionar informação da página a cada voo para exibição
      const flightsWithPageInfo = (currentPage.flights || []).map(flight => ({
        ...flight,
        pageName: currentPage.title || `Página ${currentPage.id.substring(0, 8)}` // Usar título ou ID curto
      }));
      return acc.concat(flightsWithPageInfo);
    }, [] as (FlightInfo & { pageName: string })[]);
  }, [allPages]);

  // Calculate all accommodations from all pages, including page info
  const allAccommodations = useMemo(() => {
    const result = (allPages || []).reduce((acc, currentPage) => {
      // Adicionar informação da página a cada hospedagem para exibição
      const accommodationsWithPageInfo = (currentPage.accommodations || []).map(accommodation => ({
        ...accommodation,
        pageName: currentPage.title || `Página ${currentPage.id.substring(0, 8)}` // Usar título ou ID curto
      }));
      return acc.concat(accommodationsWithPageInfo);
    }, [] as (AccommodationInfo & { pageName: string })[]);
    
    console.log('[UnifiedItemsView] allAccommodations calculado:', {
      total: result.length,
      porPagina: allPages?.map(p => ({
        pageId: p.id,
        pageTitle: p.title,
        totalAccommodations: p.accommodations?.length || 0
      }))
    });
    
    return result;
  }, [allPages]);

  // Implementação local da função moveFlightEventToPage caso ela não esteja disponível
  const localMoveFlightEventToPage = useCallback((sourcePageId: string, flightEventId: string, targetPageId: string) => {
    console.log('[UnifiedItemsView] Implementando moveFlightEventToPage localmente:', {
      sourcePageId,
      flightEventId,
      targetPageId,
      stackTrace: new Error().stack
    });
    
    // Verificar se a página de origem é a atual
    if (sourcePageId !== page.id) {
      console.error('Página de origem não corresponde à página atual');
      toast({
        title: "Erro ao mover evento",
        description: "A página de origem não corresponde à página atual.",
        variant: "destructive"
      });
      return;
    }
    
    // Encontrar o evento de voo a ser movido
    const flightEventToMove = page.flightEvents?.find(e => e.id === flightEventId);
    if (!flightEventToMove) {
      console.error('Evento de voo não encontrado');
      toast({
        title: "Erro ao mover evento",
        description: "O evento de voo não foi encontrado na página atual.",
        variant: "destructive"
      });
      return;
    }
    
    // Salvar as informações do evento para debug
    console.log('[UnifiedItemsView] Informações do evento a ser movido:', JSON.stringify(flightEventToMove));
    
    // Tentar encontrar a página destino
    const targetPage = allPages.find(p => p.id === targetPageId);
    console.log('[UnifiedItemsView] Página destino encontrada:', Boolean(targetPage), 
      targetPage ? `ID: ${targetPage.id}, Título: ${targetPage.title}` : 'Página não encontrada');
    
    // Remover o evento da página atual
    const updatedFlightEvents = (page.flightEvents || []).filter(e => e.id !== flightEventId);
    updatePage(page.id, 'flightEvents', updatedFlightEvents);
    
    // Exibir mensagem de sucesso
    toast({
      title: "Evento de voo movido",
      description: "O evento de voo foi movido para a página selecionada.",
    });
    
    console.log('[UnifiedItemsView] Evento removido da página atual. Evento será perdido pois a implementação completa não está disponível');
  }, [page, updatePage, toast, allPages]);

  // Implementação local da função moveAccommodationEventToPage caso ela não esteja disponível
  const localMoveAccommodationEventToPage = useCallback((sourcePageId: string, accommodationEventId: string, targetPageId: string) => {
    console.log('[UnifiedItemsView] Implementando moveAccommodationEventToPage localmente:', {
      sourcePageId,
      accommodationEventId,
      targetPageId,
      stackTrace: new Error().stack
    });
    
    // Verificar se a página de origem é a atual
    if (sourcePageId !== page.id) {
      console.error('Página de origem não corresponde à página atual');
      toast({
        title: "Erro ao mover evento",
        description: "A página de origem não corresponde à página atual.",
        variant: "destructive"
      });
      return;
    }
    
    // Encontrar o evento de hospedagem a ser movido
    const accommodationEventToMove = page.accommodationEvents?.find(e => e.id === accommodationEventId);
    if (!accommodationEventToMove) {
      console.error('Evento de hospedagem não encontrado');
      toast({
        title: "Erro ao mover evento",
        description: "O evento de hospedagem não foi encontrado na página atual.",
        variant: "destructive"
      });
      return;
    }
    
    // Salvar as informações do evento para debug
    console.log('[UnifiedItemsView] Informações do evento a ser movido:', JSON.stringify(accommodationEventToMove));
    
    // Tentar encontrar a página destino
    const targetPage = allPages.find(p => p.id === targetPageId);
    console.log('[UnifiedItemsView] Página destino encontrada:', Boolean(targetPage), 
      targetPage ? `ID: ${targetPage.id}, Título: ${targetPage.title}` : 'Página não encontrada');
    
    // Remover o evento da página atual
    const updatedAccommodationEvents = (page.accommodationEvents || []).filter(e => e.id !== accommodationEventId);
    updatePage(page.id, 'accommodationEvents', updatedAccommodationEvents);
    
    // Exibir mensagem de sucesso
    toast({
      title: "Evento de hospedagem movido",
      description: "O evento de hospedagem foi movido para a página selecionada.",
    });
    
    console.log('[UnifiedItemsView] Evento removido da página atual. Evento será perdido pois a implementação completa não está disponível');
  }, [page, updatePage, toast, allPages]);

  // Integração com a biblioteca
  const { addLibraryItemToQuotation } = useLibrary({
    onItemAdded: (item, type) => {
      // Adicionar item à página de cotação baseado no tipo
      if (type === 'block' && addBlock) {
        // Adicionar o bloco à página atual
        const blocks = [...(page.blocks || []), item];
        updatePage(page.id, 'blocks', blocks);
      } else if (type === 'restaurant') {
        // Adicionar o restaurante à página atual
        const restaurants = [...(page.restaurants || []), item];
        updatePage(page.id, 'restaurants', restaurants);
      } else if (type === 'accommodation') {
        // Adicionar a acomodação à página atual
        const accommodations = [...(page.accommodations || []), item];
        updatePage(page.id, 'accommodations', accommodations);
      } else if (type === 'tour') {
        // Adicionar o tour à página atual
        const tours = [...(page.tours || []), item];
        updatePage(page.id, 'tours', tours);
      } else if (type === 'transportation') {
        // Adicionar o transporte à página atual
        const transportations = [...(page.transportations || []), item];
        updatePage(page.id, 'transportations', transportations);
      } else if (type === 'flight') {
        // Adicionar o voo à página atual
        const flights = [...(page.flights || []), item];
        updatePage(page.id, 'flights', flights);
      }
    }
  });

  // Handler para abrir o browser da biblioteca
  const handleOpenLibrary = (type: string = 'all') => {
    setCurrentItemType(type);
    setIsLibraryOpen(true);
  };
  
  // Handler para selecionar um item da biblioteca
  const handleSelectLibraryItem = (item: LibraryItem) => {
    addLibraryItemToQuotation(item);
    setIsLibraryOpen(false);
  };

  // Build the unified items array whenever page data changes
  useEffect(() => {
    const newItems: UnifiedItem[] = [];
    
    // Add blocks
    if (page.blocks?.length) {
      page.blocks.forEach(block => {
        newItems.push({
          id: block.id,
          type: 'block',
          globalOrder: block.globalOrder ?? 999999,
          block
        });
      });
    }
    
    // Add flights
    if (page.flights?.length) {
      page.flights.forEach(flight => {
        if (flight.showAsSeparateEvents) {
          // Se a opção de eventos separados estiver ativada, criar itens para partida e chegada
          
          // Adicionar item de partida (sempre mostrar, mesmo sem data definida)
          newItems.push({
            id: `${flight.id}-departure`,
            type: 'flight-departure',
            globalOrder: flight.departureGlobalOrder ?? flight.globalOrder ?? 999999,
            flight,
            flightId: flight.id
          });
          
          // Adicionar item de chegada (sempre mostrar, mesmo sem data definida)
          newItems.push({
            id: `${flight.id}-arrival`,
            type: 'flight-arrival',
            globalOrder: flight.arrivalGlobalOrder ?? (flight.globalOrder ? flight.globalOrder + 0.5 : 999999),
            flight,
            flightId: flight.id
          });
        } else {
          // Comportamento padrão - adicionar como item único
          newItems.push({
            id: flight.id,
            type: 'flight',
            globalOrder: flight.globalOrder ?? 999999,
            flight
          });
        }
      });
    }
    
    // Add flight events
    if (page.flightEvents?.length) {
      page.flightEvents.forEach(event => {
        newItems.push({
          id: event.id,
          type: 'flight-event',
          globalOrder: event.globalOrder ?? 999999,
          flightEvent: event
        });
      });
    }

    // Add accommodation events
    if (page.accommodationEvents?.length) {
      page.accommodationEvents.forEach(event => {
        newItems.push({
          id: event.id,
          type: 'accommodation-event',
          globalOrder: event.globalOrder ?? 999999,
          accommodationEvent: event
        });
      });
    }
    
    // Add accommodations
    if (page.accommodations?.length) {
      page.accommodations.forEach(accommodation => {
        newItems.push({
          id: accommodation.id,
          type: 'accommodation',
          globalOrder: accommodation.globalOrder ?? 999999,
          accommodation
        });
      });
    }
    
    // Add tours
    if (page.tours?.length) {
      page.tours.forEach(tour => {
        newItems.push({
          id: tour.id,
          type: 'tour',
          globalOrder: tour.globalOrder ?? 999999,
          tour
        });
      });
    }
    
    // Add transportations
    if (page.transportations?.length) {
      page.transportations.forEach(transportation => {
        newItems.push({
          id: transportation.id,
          type: 'transportation',
          globalOrder: transportation.globalOrder ?? 999999,
          transportation
        });
      });
    }
    
    // Add restaurants
    if (page.restaurants?.length) {
      page.restaurants.forEach(restaurant => {
        newItems.push({
          id: restaurant.id,
          type: 'restaurant',
          globalOrder: restaurant.globalOrder ?? 999999,
          restaurant
        });
      });
    }
    
    // Sort by globalOrder
    newItems.sort((a, b) => a.globalOrder - b.globalOrder);
    setItems(newItems);
  }, [page]);

  // Update page with new ordering information
  const updateItemsOrder = (orderedItems: UnifiedItem[]) => {
    const blocks = [...(page.blocks || [])];
    const flights = [...(page.flights || [])];
    const accommodations = [...(page.accommodations || [])];
    const tours = [...(page.tours || [])];
    const transportations = [...(page.transportations || [])];
    const restaurants = [...(page.restaurants || [])];
    const flightEvents = [...(page.flightEvents || [])];
    const accommodationEvents = [...(page.accommodationEvents || [])];
    
    // Update globalOrder in each collection
    orderedItems.forEach((item, index) => {
      if (item.type === 'block') {
        const blockIndex = blocks.findIndex(b => b.id === item.id);
        if (blockIndex >= 0) {
          blocks[blockIndex].globalOrder = index;
        }
      } else if (item.type === 'flight') {
        const flightIndex = flights.findIndex(f => f.id === item.id);
        if (flightIndex >= 0) {
          flights[flightIndex].globalOrder = index;
        }
      } else if (item.type === 'accommodation') {
        const accommodationIndex = accommodations.findIndex(a => a.id === item.id);
        if (accommodationIndex >= 0) {
          accommodations[accommodationIndex].globalOrder = index;
        }
      } else if (item.type === 'accommodation-checkin') {
        // Extrair o ID original da hospedagem do ID do item de check-in
        const accommodationId = item.accommodationId || item.id.replace('-checkin', '');
        const accommodationIndex = accommodations.findIndex(a => a.id === accommodationId);
        if (accommodationIndex >= 0) {
          accommodations[accommodationIndex].checkInGlobalOrder = index;
        }
      } else if (item.type === 'accommodation-checkout') {
        // Extrair o ID original da hospedagem do ID do item de check-out
        const accommodationId = item.accommodationId || item.id.replace('-checkout', '');
        const accommodationIndex = accommodations.findIndex(a => a.id === accommodationId);
        if (accommodationIndex >= 0) {
          accommodations[accommodationIndex].checkOutGlobalOrder = index;
        }
      } else if (item.type === 'tour') {
        const tourIndex = tours.findIndex(t => t.id === item.id);
        if (tourIndex >= 0) {
          tours[tourIndex].globalOrder = index;
        }
      } else if (item.type === 'transportation') {
        const transportationIndex = transportations.findIndex(t => t.id === item.id);
        if (transportationIndex >= 0) {
          transportations[transportationIndex].globalOrder = index;
        }
      } else if (item.type === 'restaurant') {
        const restaurantIndex = restaurants.findIndex(r => r.id === item.id);
        if (restaurantIndex >= 0) {
          restaurants[restaurantIndex].globalOrder = index;
        }
      } else if (item.type === 'flight-departure') {
        // Extract the original flight ID from the departure item ID
        const flightId = item.flightId || item.id.replace('-departure', '');
        const flightIndex = flights.findIndex(f => f.id === flightId);
        if (flightIndex >= 0) {
          flights[flightIndex].departureGlobalOrder = index;
        }
      } else if (item.type === 'flight-arrival') {
        // Extract the original flight ID from the arrival item ID
        const flightId = item.flightId || item.id.replace('-arrival', '');
        const flightIndex = flights.findIndex(f => f.id === flightId);
        if (flightIndex >= 0) {
          flights[flightIndex].arrivalGlobalOrder = index;
        }
      } else if (item.type === 'flight-event' && item.flightEvent) {
        const eventIndex = flightEvents.findIndex(e => e.id === item.id);
        if (eventIndex >= 0) {
          flightEvents[eventIndex].globalOrder = index;
        }
      } else if (item.type === 'accommodation-event' && item.accommodationEvent) {
        const eventIndex = accommodationEvents.findIndex(e => e.id === item.id);
        if (eventIndex >= 0) {
          accommodationEvents[eventIndex].globalOrder = index;
        }
      }
    });
    
    // Update each collection in the page state
    updatePage(page.id, 'blocks', blocks);
    updatePage(page.id, 'flights', flights);
    updatePage(page.id, 'accommodations', accommodations);
    updatePage(page.id, 'tours', tours);
    updatePage(page.id, 'transportations', transportations);
    updatePage(page.id, 'restaurants', restaurants);
    updatePage(page.id, 'flightEvents', flightEvents);
    updatePage(page.id, 'accommodationEvents', accommodationEvents);
  };

  // Toggle item expansion
  const toggleItem = (itemId: string) => {
    setActiveItemId(prevActiveItemId => {
      // If this item is already active, collapse it by setting activeItemId to null
      if (prevActiveItemId === itemId) {
        return null;
      }
      // Otherwise, expand this item and collapse others
      return itemId;
    });
  };

  // Wrap handleDragEnd to ensure drag operations complete properly
  const handleDragEnd = (result: DropResult) => {
    const { destination, source } = result;
    
    // If dropped outside the list or at the same position, do nothing
    if (!destination || (destination.index === source.index)) {
      return;
    }
    
    // Clone the current items array
    const newItems = Array.from(items);
    
    // Remove the item from its source position
    const [removedItem] = newItems.splice(source.index, 1);
    
    // Insert the item at its destination position
    newItems.splice(destination.index, 0, removedItem);
    
    // Update global order for all items
    const orderedItems = newItems.map((item, index) => ({
      ...item,
      globalOrder: index
    }));
    
    // Set local state before updating parent state to ensure UI is responsive
    setItems(orderedItems);
    
    // Use setTimeout to ensure drag animation completes before updating parent state
    setTimeout(() => {
      // Update original data structures with new ordering
      updateItemsOrder(orderedItems);
    }, 0);
  };

  // Create a memoized drag handle component to prevent re-renders
  const DragHandle = memo(({ dragHandleProps }: { dragHandleProps: any }) => (
  <div 
    {...dragHandleProps}
    className="p-3 cursor-grab hover:bg-gray-100 rounded-l flex-shrink-0 flex items-center justify-center h-full"
    style={{ touchAction: 'none' }}
  >
    <GripVertical className="h-5 w-5 text-gray-400" />
  </div>
));

  // Get item title based on type
  const getItemTitle = (item: UnifiedItem): string => {
    if (item.type === 'block') {
      return item.block?.title || 'Bloco';
    } else if (item.type === 'flight') {
      return item.flight?.title || 'Voo';
    } else if (item.type === 'accommodation') {
      return item.accommodation?.name || 'Hospedagem';
    } else if (item.type === 'tour') {
      return item.tour?.name || 'Passeio';
    } else if (item.type === 'transportation') {
      return item.transportation?.name || 'Traslado';
    } else if (item.type === 'restaurant') {
      return item.restaurant?.name || 'Restaurante';
    }
    return 'Item';
  };

  // Remove an item
  const handleRemoveItem = (item: UnifiedItem) => {
    if (item.type === 'block' && deleteBlock) {
      deleteBlock(page.id, item.id);
      } else {
      removeItem(item.id, item.type as string);
    }
  };

  // Render item based on type
  const renderItem = (item: UnifiedItem) => {
    if (item.type === 'block' && item.block) {
      return renderBlockForm(item.block, items.findIndex(i => i.id === item.id));
    } else if (item.type === 'flight' && item.flight) {
      return renderFlightForm(item.flight, items.findIndex(i => i.id === item.id));
    } else if (item.type === 'accommodation' && item.accommodation) {
      return renderAccommodationForm(item.accommodation, items.findIndex(i => i.id === item.id));
    } else if (item.type === 'tour' && item.tour) {
      return renderTourForm(item.tour, items.findIndex(i => i.id === item.id));
    } else if (item.type === 'transportation' && item.transportation) {
      return renderTransportationForm(item.transportation, items.findIndex(i => i.id === item.id));
    } else if (item.type === 'restaurant' && item.restaurant) {
      return renderRestaurantForm(item.restaurant, items.findIndex(i => i.id === item.id));
    } else if (item.type === 'flight-departure' && item.flight) {
      return renderFlightEventItem(item.flight, 'departure', items.findIndex(i => i.id === item.id));
    } else if (item.type === 'flight-arrival' && item.flight) {
      return renderFlightEventItem(item.flight, 'arrival', items.findIndex(i => i.id === item.id));
    } else if (item.type === 'flight-event' && item.flightEvent) {
      return renderFlightEventForm(item.flightEvent, items.findIndex(i => i.id === item.id));
    } else if (item.type === 'accommodation-event' && item.accommodationEvent) {
      return renderAccommodationEventForm(item.accommodationEvent, items.findIndex(i => i.id === item.id));
    }
    return null;
  };

  // Render a block form
  const renderBlockForm = (block: QuotationBlock, index: number) => {
    const updateLocalBlock = (field: keyof QuotationBlock, value: any) => {
      updateBlock(page.id, block.id, field, value);
    };

    const deleteLocalBlock = () => {
      deleteBlock(page.id, block.id);
    };

    // Adicionar função para iniciar o movimento do bloco
    const initiateBlockMove = (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevenir propagação do evento
      handleInitiateBlockMove(block.id);
    };

    return (
      <QuotationBlockComponent
        key={block.id}
        block={block}
        index={index}
        pageId={page.id}
        updateBlock={updateBlock}
        deleteBlock={deleteBlock}
        isCollapsed={activeItemId !== block.id}
        onToggleCollapse={() => toggleItem(block.id)}
        moveBlockToPage={moveBlockToPage}
        allPages={allPages}
      />
    );
  };

  // Render a flight form
  const renderFlightForm = (flight: FlightInfo, index: number) => {
    const updateFlight = (pageId: string, flightId: string, field: keyof FlightInfo, value: any) => {
      const updatedFlights = [...(page.flights || [])];
      const flightIndex = updatedFlights.findIndex(f => f.id === flightId);
      if (flightIndex >= 0) {
        updatedFlights[flightIndex] = {
          ...updatedFlights[flightIndex],
          [field]: value
        };
        updatePage(page.id, 'flights', updatedFlights);
      }
    };

    const deleteFlight = (pageId: string, flightId: string) => {
      removeItem(flightId, 'flight');
    };

    const updateFlightConnection = (pageId: string, flightId: string, connectionId: string, field: string, value: any) => {
      const updatedFlights = [...(page.flights || [])];
      const flightIndex = updatedFlights.findIndex(f => f.id === flightId);
      
      if (flightIndex >= 0 && updatedFlights[flightIndex].connections) {
        const connectionIndex = updatedFlights[flightIndex].connections.findIndex(c => c.id === connectionId);
        
        if (connectionIndex >= 0) {
          updatedFlights[flightIndex].connections[connectionIndex] = {
            ...updatedFlights[flightIndex].connections[connectionIndex],
            [field]: value
          };
          
          updatePage(page.id, 'flights', updatedFlights);
        }
      }
    };

    const addFlightConnection = (pageId: string, flightId: string) => {
      const updatedFlights = [...(page.flights || [])];
      const flightIndex = updatedFlights.findIndex(f => f.id === flightId);
      
      if (flightIndex >= 0) {
        const flight = updatedFlights[flightIndex];
        const currentDate = new Date();
        const formattedDate = format(currentDate, "yyyy-MM-dd");
        
        // Create a new connection with default values
        const newConnection: FlightConnection = {
          id: `connection-${Date.now()}`,
          airline: '',
          flightNumber: '',
          departureAirport: flight.arrivalAirport,
          departureDateTime: flight.arrivalDateTime || `${formattedDate}T00:00:00`,
          arrivalAirport: '',
          arrivalDateTime: flight.arrivalDateTime ? 
            new Date(new Date(flight.arrivalDateTime).getTime() + 2 * 60 * 60 * 1000).toISOString() : 
            `${formattedDate}T02:00:00`,
          flightDuration: '',
          notes: ''
        };
        
        updatedFlights[flightIndex] = {
          ...flight,
          connections: [...(flight.connections || []), newConnection]
        };
        
        updatePage(page.id, 'flights', updatedFlights);
      }
    };

    const removeFlightConnection = (pageId: string, flightId: string, connectionId: string) => {
      const updatedFlights = [...(page.flights || [])];
      const flightIndex = updatedFlights.findIndex(f => f.id === flightId);
      
      if (flightIndex >= 0 && updatedFlights[flightIndex].connections) {
        updatedFlights[flightIndex] = {
          ...updatedFlights[flightIndex],
          connections: updatedFlights[flightIndex].connections.filter(c => c.id !== connectionId)
        };
        
        updatePage(page.id, 'flights', updatedFlights);
      }
    };
    
    return (
      <FlightForm
        flight={flight}
        index={index}
        pageId={page.id}
        updateFlight={updateFlight}
        updateFlightConnection={updateFlightConnection}
        addFlightConnection={addFlightConnection}
        removeFlightConnection={removeFlightConnection}
        deleteFlight={deleteFlight}
        isCollapsed={activeItemId !== flight.id}
        onToggleCollapse={() => toggleItem(flight.id)}
        searchFlight={() => Promise.resolve()}
        searchConnectionFlight={() => Promise.resolve()}
      />
    );
  };

  // Render an accommodation form
  const renderAccommodationForm = (accommodation: AccommodationInfo, index: number) => {
    const updateAccommodation = (pageId: string, accommodationId: string, field: keyof AccommodationInfo, value: any) => {
      const updatedAccommodations = [...(page.accommodations || [])];
      const index = updatedAccommodations.findIndex(a => a.id === accommodationId);
      if (index >= 0) {
        updatedAccommodations[index] = {
          ...updatedAccommodations[index],
          [field]: value
        };
        updatePage(page.id, 'accommodations', updatedAccommodations);
      }
    };

    const deleteAccommodation = (pageId: string, accommodationId: string) => {
      removeItem(accommodationId, 'accommodation');
    };

    // Adicionar batchUpdateAccommodation para permitir atualizações em lote
    const batchUpdateAccommodation = (accommodationId: string, updates: Partial<AccommodationInfo>) => {
      const updatedAccommodations = [...(page.accommodations || [])];
      const index = updatedAccommodations.findIndex(a => a.id === accommodationId);
      
      if (index >= 0) {
        // Criar uma cópia profunda da acomodação com as atualizações aplicadas
        updatedAccommodations[index] = {
          ...updatedAccommodations[index],
          ...updates
        };
        
        // Log para debug
        console.log('[UnifiedItemsView] batchUpdateAccommodation called:', {
          accommodationId,
          updates,
          hasCoordinates: !!updates.coordinates
        });
        
        updatePage(page.id, 'accommodations', updatedAccommodations);
      }
    };

    return (
      <AccommodationFormComponent
        key={accommodation.id}
        accommodation={accommodation}
        index={index}
        pageId={page.id}
        updateAccommodation={updateAccommodation}
        batchUpdateAccommodation={batchUpdateAccommodation}
        deleteAccommodation={deleteAccommodation}
        moveAccommodationToPage={handleInitiateAccommodationMove}
        isCollapsed={activeItemId !== accommodation.id}
        onToggleCollapse={() => toggleItem(accommodation.id)}
      />
    );
  };

  // Render a tour form
  const renderTourForm = (tour: TourInfo, index: number) => {
    const updateTour = (pageId: string, tourId: string, field: keyof TourInfo, value: any) => {
      const updatedTours = [...(page.tours || [])];
      const index = updatedTours.findIndex(t => t.id === tourId);
      if (index >= 0) {
        updatedTours[index] = {
          ...updatedTours[index],
          [field]: value
        };
        updatePage(page.id, 'tours', updatedTours);
      }
    };

    const deleteTour = (pageId: string, tourId: string) => {
      removeItem(tourId, 'tour');
    };

    // Adicionar batchUpdateTour para permitir atualizações em lote
    const batchUpdateTour = (tourId: string, updates: Partial<TourInfo>) => {
      const updatedTours = [...(page.tours || [])];
      const index = updatedTours.findIndex(t => t.id === tourId);
      
      if (index >= 0) {
        // Criar uma cópia profunda do tour com as atualizações aplicadas
        updatedTours[index] = {
          ...updatedTours[index],
          ...updates
        };
        
        // Log para debug
        console.log('[UnifiedItemsView] batchUpdateTour called:', {
          tourId,
          updates,
          hasCoordinates: !!updates.coordinates
        });
        
        updatePage(page.id, 'tours', updatedTours);
      }
    };

    // Function to initiate moving a tour to another page
    const handleInitiateTourMove = (tourId: string) => {
      // Check if we have the necessary props and data
      if (!moveTourToPage || !allPages || allPages.length <= 1) {
        toast({
          title: "Operação não disponível",
          description: "É necessário ter mais de uma página disponível para mover o passeio.",
        });
        return;
      }
      
      // Calculate available pages (excluding current page)
      const availablePages = allPages.filter(p => p.id !== page.id);
      
      if (availablePages.length === 0) {
        toast({
          title: "Operação não disponível",
          description: "É necessário ter mais de uma página disponível para mover o passeio.",
        });
        return;
      }
      
      // Open the page selection modal
      setSelectedTourId(tourId);
      setSelectionType('tour');
      setIsPageSelectionOpen(true);
    };

    return (
      <TourForm
        tour={tour}
        index={index}
        pageId={page.id}
        updateTour={updateTour}
        batchUpdateTour={batchUpdateTour}
        deleteTour={deleteTour}
        moveTourToPage={handleInitiateTourMove}
        isCollapsed={activeItemId !== tour.id}
        onToggleCollapse={() => toggleItem(tour.id)}
      />
    );
  };

  // Render a transportation form
  const renderTransportationForm = (transportation: TransportationInfo, index: number) => {
    const updateTransportation = (pageId: string, transportationId: string, field: keyof TransportationInfo, value: any) => {
      const updatedTransportations = [...(page.transportations || [])];
      const index = updatedTransportations.findIndex(t => t.id === transportationId);
      if (index >= 0) {
        updatedTransportations[index] = {
          ...updatedTransportations[index],
          [field]: value
        };
        updatePage(page.id, 'transportations', updatedTransportations);
      }
    };

    const deleteTransportation = (pageId: string, transportationId: string) => {
      removeItem(transportationId, 'transportation');
    };

    // Adicionar batchUpdateTransportation para permitir atualizações em lote
    const batchUpdateTransportation = (transportationId: string, updates: Partial<TransportationInfo>) => {
      const updatedTransportations = [...(page.transportations || [])];
      const index = updatedTransportations.findIndex(t => t.id === transportationId);
      
      if (index >= 0) {
        // Criar uma cópia profunda do transporte com as atualizações aplicadas
        updatedTransportations[index] = {
          ...updatedTransportations[index],
          ...updates
        };
        
        // Log para debug
        console.log('[UnifiedItemsView] batchUpdateTransportation called:', {
          transportationId,
          updates,
          hasCoordinates: !!updates.coordinates
        });
        
        updatePage(page.id, 'transportations', updatedTransportations);
      }
    };

    // Function to initiate moving a transportation to another page
    const handleInitiateTransportationMove = (transportationId: string) => {
      // Check if we have the necessary props and data
      if (!moveTransportationToPage || !allPages || allPages.length <= 1) {
        toast({
          title: "Operação não disponível",
          description: "É necessário ter mais de uma página disponível para mover o traslado.",
        });
        return;
      }
      
      // Determine available pages for moving
      const availablePages = allPages.filter(p => p.id !== page.id);
      
      console.log('[UnifiedItemsView] Available pages:', {
        count: availablePages.length,
        pageIds: availablePages.map(p => p.id),
        excludedPageId: page.id,
        allPagesOriginal: allPages
      });
      
      if (availablePages.length === 0) {
        console.log('[UnifiedItemsView] No available pages after filtering');
        toast({
          title: "Operação não disponível",
          description: "É necessário ter mais de uma página disponível para mover o traslado.",
        });
        return;
      }
      
      // Open the page selection modal
      setSelectionType('transportation');
      console.log('[UnifiedItemsView] Opening page selection modal for transportation:', transportationId);
      setSelectedTransportationId(transportationId);
      setIsPageSelectionOpen(true);
    };

    return (
      <TransportationForm
        transportation={transportation}
        index={index}
        pageId={page.id}
        updateTransportation={updateTransportation}
        batchUpdateTransportation={batchUpdateTransportation}
        deleteTransportation={deleteTransportation}
        moveTransportationToPage={handleInitiateTransportationMove}
        isCollapsed={activeItemId !== transportation.id}
        onToggleCollapse={() => toggleItem(transportation.id)}
      />
    );
  };

  // Render a restaurant form
  const renderRestaurantForm = (restaurant: RestaurantInfo, index: number) => {
    const updateRestaurant = (pageId: string, restaurantId: string, field: keyof RestaurantInfo, value: any) => {
      // Create a copy of the restaurants array
      const restaurants = [...(page.restaurants || [])];
      
      // Find the restaurant and update the field
      const restaurantIndex = restaurants.findIndex(r => r.id === restaurantId);
      if (restaurantIndex >= 0) {
        restaurants[restaurantIndex] = { ...restaurants[restaurantIndex], [field]: value };
        
        // Update the page
        updatePage(page.id, 'restaurants', restaurants);
      }
    };
    
    const deleteRestaurant = (pageId: string, restaurantId: string) => {
      // Remove the restaurant from the array
      removeItem(restaurantId, 'restaurant');
    };

    // Adicionar batchUpdateRestaurant para permitir atualizações em lote
    const batchUpdateRestaurant = (restaurantId: string, updates: Partial<RestaurantInfo>) => {
      const restaurants = [...(page.restaurants || [])];
      const index = restaurants.findIndex(r => r.id === restaurantId);
      
      if (index >= 0) {
        // Criar uma cópia profunda do restaurante com as atualizações aplicadas
        restaurants[index] = {
          ...restaurants[index],
          ...updates
        };
        
        // Log para debug
        console.log('[UnifiedItemsView] batchUpdateRestaurant called:', {
          restaurantId,
          updates,
          hasCoordinates: !!updates.coordinates
        });
        
        updatePage(page.id, 'restaurants', restaurants);
      }
    };

    // Function to initiate moving a restaurant to another page
    const handleInitiateRestaurantMove = (restaurantId: string) => {
      // Check if we have the necessary props and data
      if (!moveRestaurantToPage || !allPages || allPages.length <= 1) {
        toast({
          title: "Operação não disponível",
          description: "É necessário ter mais de uma página disponível para mover o restaurante.",
        });
        return;
      }
      
      // Calculate available pages (excluding current page)
      const availablePages = allPages.filter(p => p.id !== page.id);
      
      if (availablePages.length === 0) {
        toast({
          title: "Operação não disponível",
          description: "É necessário ter mais de uma página disponível para mover o restaurante.",
        });
        return;
      }
      
      // Open the page selection modal
      setSelectedRestaurantId(restaurantId);
      setSelectionType('restaurant');
      setIsPageSelectionOpen(true);
    };

    // Return the RestaurantForm component
    return (
      <RestaurantForm
        key={restaurant.id}
        restaurant={restaurant}
        index={index}
        pageId={page.id}
        updateRestaurant={updateRestaurant}
        batchUpdateRestaurant={batchUpdateRestaurant}
        deleteRestaurant={deleteRestaurant}
        moveRestaurantToPage={handleInitiateRestaurantMove}
        isCollapsed={activeItemId !== restaurant.id}
        onToggleCollapse={() => toggleItem(restaurant.id)}
      />
    );
  };

  // Render accommodation event (check-in/check-out) item
  const renderAccommodationEventItem = (accommodation: AccommodationInfo, eventType: 'checkin' | 'checkout', index: number) => {
    const updateAccommodation = (pageId: string, accommodationId: string, field: keyof AccommodationInfo, value: any) => {
      const updatedAccommodations = [...(page.accommodations || [])];
      const index = updatedAccommodations.findIndex(a => a.id === accommodationId);
      if (index >= 0) {
        updatedAccommodations[index] = {
          ...updatedAccommodations[index],
          [field]: value
        };
        updatePage(page.id, 'accommodations', updatedAccommodations);
      }
    };

    const deleteAccommodationEvent = (pageId: string, accommodationId: string) => {
      // Apenas remover o status de eventos separados em vez de excluir a hospedagem
      const updatedAccommodations = [...(page.accommodations || [])];
      const index = updatedAccommodations.findIndex(a => a.id === accommodationId);
      if (index >= 0) {
        updatedAccommodations[index] = {
          ...updatedAccommodations[index],
          showAsSeparateEvents: false
        };
        updatePage(page.id, 'accommodations', updatedAccommodations);
        
        toast({
          title: "Modo de eventos separados desativado",
          description: `A hospedagem ${accommodation.name} agora será exibida como um único item.`,
        });
      }
    };

    const itemId = eventType === 'checkin' 
      ? `${accommodation.id}-checkin`
      : `${accommodation.id}-checkout`;

    return (
      <AccommodationEventItemComponent
        accommodation={accommodation}
        eventType={eventType}
        index={index}
        pageId={page.id}
        isCollapsed={activeItemId !== itemId}
        onToggleCollapse={() => toggleItem(itemId)}
        updateAccommodation={updateAccommodation}
        deleteAccommodation={deleteAccommodationEvent}
      />
    );
  };

  // Render flight event (departure/arrival) item
  const renderFlightEventItem = (flight: FlightInfo, eventType: 'departure' | 'arrival', index: number) => {
    const updateFlight = (pageId: string, flightId: string, field: keyof FlightInfo, value: any) => {
      const updatedFlights = [...(page.flights || [])];
      const index = updatedFlights.findIndex(f => f.id === flightId);
      if (index >= 0) {
        updatedFlights[index] = {
          ...updatedFlights[index],
          [field]: value
        };
        updatePage(page.id, 'flights', updatedFlights);
      }
    };

    const deleteFlightEvent = (pageId: string, flightId: string) => {
      // Apenas remover o status de eventos separados em vez de excluir o voo
      const updatedFlights = [...(page.flights || [])];
      const index = updatedFlights.findIndex(f => f.id === flightId);
      if (index >= 0) {
        updatedFlights[index] = {
          ...updatedFlights[index],
          showAsSeparateEvents: false
        };
        updatePage(page.id, 'flights', updatedFlights);
        
        const flightName = `${flight.airline || ''} ${flight.flightNumber || ''}`;
        toast({
          title: "Modo de eventos separados desativado",
          description: `O voo ${flightName} agora será exibido como um único item.`,
        });
      }
    };

    const itemId = eventType === 'departure' 
      ? `${flight.id}-departure`
      : `${flight.id}-arrival`;

    return (
      <FlightEventItem
        flight={flight}
        eventType={eventType}
        index={index}
        pageId={page.id}
        isCollapsed={activeItemId !== itemId}
        onToggleCollapse={() => toggleItem(itemId)}
        updateFlight={updateFlight}
        deleteFlight={deleteFlightEvent}
      />
    );
  };

  // Render flight event form
  const renderFlightEventForm = (flightEvent: any, index: number) => {
    console.log('[UnifiedItemsView] Renderizando FlightEventForm:', {
      flightEventId: flightEvent.id,
      pageId: page.id,
      flightId: flightEvent.flightId,
      hasCoordinates: Boolean(flightEvent._coordinates || flightEvent.coordinates),
      coordinates: flightEvent._coordinates || flightEvent.coordinates,
      allPagesAvailable: Boolean(allPages),
      numberOfPages: allPages?.length,
      moveFlightEventToPageAvailable: Boolean(moveFlightEventToPage),
      moveFlightEventToPageType: typeof moveFlightEventToPage
    });
    
    // Verificar se este FlightEvent tem as propriedades necessárias para garantir persistência
    // Se o flightEvent não tiver um tipo definido corretamente, podemos ter problemas de renderização
    if (flightEvent && typeof flightEvent.isDeparture !== 'boolean') {
      console.warn('[UnifiedItemsView] FlightEvent com propriedade isDeparture indefinida:', flightEvent.id);
      // Definir um valor padrão para evitar problemas
      flightEvent.isDeparture = true;
    }
    
    // Buscar o voo associado para garantir que as informações estejam disponíveis
    const associatedFlight = page.flights?.find(f => f.id === flightEvent.flightId);
    
    // Caso o voo seja encontrado, verificamos se precisamos extrair as coordenadas
    if (associatedFlight) {
      // Registrar que encontramos o voo para facilitar debug
      console.log('[UnifiedItemsView] Voo associado encontrado para FlightEvent:', {
        flightEventId: flightEvent.id,
        flightId: associatedFlight.id,
        airline: associatedFlight.airline,
        flightNumber: associatedFlight.flightNumber
      });
      
      // Extrair coordenadas do voo com base em isDeparture
      const coordinates = flightEvent.isDeparture 
        ? associatedFlight.departureCoordinates 
        : associatedFlight.arrivalCoordinates;
        
      // Se encontrarmos coordenadas, atualizamos o flightEvent imediatamente
      if (coordinates && coordinates.lat && coordinates.lng) {
        console.log('[UnifiedItemsView] Encontradas coordenadas do voo associado:', coordinates);
        
        // Atualizar o flightEvent local para exibição imediata
        flightEvent._coordinates = coordinates;
        flightEvent.coordinates = coordinates;
        
        // Imediatamente atualizar o flightEvent na página para persistir as coordenadas
        const updatedFlightEvents = [...(page.flightEvents || [])];
        const eventIndex = updatedFlightEvents.findIndex(e => e.id === flightEvent.id);
        if (eventIndex >= 0) {
          // Apenas atualizar se não tiver coordenadas ainda ou se forem diferentes
          const needsUpdate = 
            !updatedFlightEvents[eventIndex]._coordinates || 
            updatedFlightEvents[eventIndex]._coordinates.lat !== coordinates.lat || 
            updatedFlightEvents[eventIndex]._coordinates.lng !== coordinates.lng;
            
          if (needsUpdate) {
            updatedFlightEvents[eventIndex] = {
              ...updatedFlightEvents[eventIndex],
              _coordinates: coordinates,
              coordinates: coordinates
            };
            
            // Atualizar os flightEvents da página para preservar as coordenadas
            console.log('[UnifiedItemsView] Atualizando FlightEvent com coordenadas na página');
            updatePage(page.id, 'flightEvents', updatedFlightEvents);
          }
        }
      }
    } else if (flightEvent.flightId) {
      // Se não encontrarmos o voo mas temos um flightId, registrar isso para debug
      console.warn('[UnifiedItemsView] Voo não encontrado para FlightEvent, mas flightId existe:', {
        flightEventId: flightEvent.id,
        flightId: flightEvent.flightId,
        availableFlightsCount: page.flights?.length || 0
      });
    }
    
    const updateFlightEvent = (pageId: string, flightEventId: string, field: string, value: any) => {
      const updatedFlightEvents = [...(page.flightEvents || [])];
      const eventIndex = updatedFlightEvents.findIndex(e => e.id === flightEventId);
      if (eventIndex >= 0) {
        const updatedEvent = {
          ...updatedFlightEvents[eventIndex],
          [field]: value
        };
        
        updatedFlightEvents[eventIndex] = updatedEvent;
        
        // If updating flightId or isDeparture, we need to update coordinates
        if ((field === 'flightId' || field === 'isDeparture') && updatedEvent.flightId) {
          const flight = page.flights?.find(f => f.id === updatedEvent.flightId);
          if (flight) {
            const coordinates = updatedEvent.isDeparture 
              ? flight.departureCoordinates 
              : flight.arrivalCoordinates;
              
            if (coordinates && coordinates.lat && coordinates.lng) {
              console.log('[UnifiedItemsView] Atualizando coordenadas após mudança de voo ou tipo de evento:', {
                flightEventId,
                flightId: updatedEvent.flightId,
                isDeparture: updatedEvent.isDeparture,
                field,
                coordinates
              });
              
              updatedFlightEvents[eventIndex]._coordinates = coordinates;
              updatedFlightEvents[eventIndex].coordinates = coordinates;
            } else {
              console.warn('[UnifiedItemsView] Voo não possui coordenadas válidas para o tipo de evento:', {
                flightId: updatedEvent.flightId,
                isDeparture: updatedEvent.isDeparture,
                hasDepCoords: Boolean(flight.departureCoordinates),
                hasArrCoords: Boolean(flight.arrivalCoordinates)
              });
            }
          }
        }
        
        updatePage(page.id, 'flightEvents', updatedFlightEvents);
      }
    };

    const deleteFlightEvent = (pageId: string, flightEventId: string) => {
      removeItem(flightEventId, 'flight-event');
    };

    // Verificar se há páginas disponíveis para mover
    const hasAvailablePages = allPages && allPages.length > 1 && allPages.some(p => p.id !== page.id);
    
    console.log('[UnifiedItemsView] Verificação de disponibilidade:', {
      hasAvailablePages,
      allPagesLength: allPages?.length,
      currentPageId: page.id,
      hasMoveFlightEventToPage: Boolean(moveFlightEventToPage)
    });
    
    // Simplificar a lógica - apenas verificar se existem páginas disponíveis para mover
    // A verificação da função moveFlightEventToPage será feita no momento do clique
    const handleFlightEventMove = hasAvailablePages ? 
      (flightEventId: string) => {
        console.log("Iniciando movimentação de flight event", flightEventId);
        
        // Abrir o modal de seleção de página
        setSelectionType('flightEvent');
        setSelectedFlightEventId(flightEventId);
        setIsPageSelectionOpen(true);
      } 
      : undefined;

    // Log para verificar a lista allFlights passada
    console.log('[UnifiedItemsView] Passando para FlightEventForm:', {
      flightEventId: flightEvent.id,
      totalAllFlights: allFlights.length,
      // Exemplo dos primeiros 3 voos para verificar
      sampleFlights: allFlights.slice(0, 3).map(f => ({ id: f.id, airline: f.airline, pageName: f.pageName }))
    });

    return (
      <FlightEventForm
        flightEvent={flightEvent}
        index={index}
        pageId={page.id}
        flights={allFlights} // <-- Passar a lista completa aqui
        updateFlightEvent={updateFlightEvent}
        deleteFlightEvent={deleteFlightEvent}
        moveFlightEvent={handleFlightEventMove}
        isCollapsed={activeItemId !== flightEvent.id}
        onToggleCollapse={() => toggleItem(flightEvent.id)}
        // Passar allPages também, caso seja necessário em FlightEventForm para outros fins
        allPages={allPages}
      />
    );
  };

  // Render accommodation event form
  const renderAccommodationEventForm = (accommodationEvent: any, index: number) => {
    console.log('[UnifiedItemsView] Renderizando AccommodationEventForm:', {
      accommodationEventId: accommodationEvent.id,
      pageId: page.id,
      allPagesAvailable: Boolean(allPages),
      numberOfPages: allPages?.length,
      moveAccommodationEventToPageAvailable: Boolean(moveAccommodationEventToPage),
      moveAccommodationEventToPageType: typeof moveAccommodationEventToPage,
      totalAccommodations: allAccommodations.length,
      currentAccommodationId: accommodationEvent.accommodationId,
      eventTitle: accommodationEvent.title,
      accommodationEventIsCheckIn: accommodationEvent.isCheckIn
    });
    
    // Alterar completamente a função de updateAccommodationEvent para garantir que o accommodationId seja preservado
    const updateAccommodationEvent = (pageId: string, accommodationEventId: string, field: string, value: any) => {
      // Encontrar o evento atual para obter o estado atual antes de qualquer modificação
      const currentEvents = [...(page.accommodationEvents || [])];
      const eventIndex = currentEvents.findIndex(e => e.id === accommodationEventId);
      
      if (eventIndex < 0) {
        console.error('[UnifiedItemsView] Evento não encontrado para atualização:', accommodationEventId);
        return;
      }
      
      // Obter o evento atual antes de qualquer modificação
      const currentEvent = currentEvents[eventIndex];
      
      // Para determinar o accommodationId a usar no log, precisamos considerar se estamos atualizando esse campo
      const effectiveAccommodationId = field === 'accommodationId' ? value : currentEvent.accommodationId;
      
      console.log('[UnifiedItemsView] Atualizando evento de hospedagem:', {
        field,
        value,
        eventId: accommodationEventId,
        accommodationId: effectiveAccommodationId,
        currentAccommodationId: currentEvent.accommodationId
      });
      
      // Criar uma cópia do evento com todas as propriedades existentes preservadas
      const updatedEvent = {
        ...currentEvent,
          [field]: value
        };
      
      console.log('[UnifiedItemsView] Estado do evento antes/depois:', {
        before: {
          accommodationId: currentEvent.accommodationId,
          title: currentEvent.title,
          isCheckIn: currentEvent.isCheckIn
        },
        after: {
          accommodationId: updatedEvent.accommodationId,
          title: updatedEvent.title,
          isCheckIn: updatedEvent.isCheckIn
        }
      });
      
      // Se estamos atualizando o accommodationId, também devemos atualizar o título
      if (field === 'accommodationId' && value) {
        console.log('[UnifiedItemsView] Nova hospedagem selecionada, persistindo ID:', value);
        
        // Verifica se a acomodação existe
        const selectedAccommodation = allAccommodations.find(a => a.id === value);
        if (selectedAccommodation) {
          console.log('[UnifiedItemsView] Hospedagem encontrada:', {
            id: selectedAccommodation.id,
            name: selectedAccommodation.name,
            page: selectedAccommodation.pageName
          });
          
          // Atualiza o título automaticamente
          const eventType = updatedEvent.isCheckIn ? 'Check-in' : 'Check-out';
          updatedEvent.title = `${eventType} - ${selectedAccommodation.name}`;
          
          console.log('[UnifiedItemsView] Título atualizado durante mudança de hospedagem:', updatedEvent.title);
        } else {
          console.warn('[UnifiedItemsView] Hospedagem não encontrada para ID:', value);
        }
      }
      
      // Se estamos atualizando o isCheckIn, devemos atualizar o título se tivermos um accommodationId
      if (field === 'isCheckIn' && updatedEvent.accommodationId) {
        const selectedAccommodation = allAccommodations.find(a => a.id === updatedEvent.accommodationId);
        if (selectedAccommodation) {
          const eventType = value ? 'Check-in' : 'Check-out';
          updatedEvent.title = `${eventType} - ${selectedAccommodation.name}`;
          console.log('[UnifiedItemsView] Título atualizado durante mudança de tipo (check-in/check-out):', updatedEvent.title);
        }
      }
      
      // Salvar de volta no array
      currentEvents[eventIndex] = updatedEvent;
      
      // Atualizar o estado da página
      updatePage(page.id, 'accommodationEvents', currentEvents);
      
      // Verificar logo após a atualização para garantir que o estado seja persistido corretamente
      setTimeout(() => {
        // A página já está disponível no escopo como 'page'
        const eventsAfterUpdate = page.accommodationEvents || [];
        const eventAfterUpdate = eventsAfterUpdate.find(e => e.id === accommodationEventId);
        
        console.log('[UnifiedItemsView] Verificação após atualização:', {
          eventId: accommodationEventId,
          updatedEvent: eventAfterUpdate,
          accommodationId: eventAfterUpdate?.accommodationId,
          title: eventAfterUpdate?.title,
          expectedAccommodationId: field === 'accommodationId' ? value : currentEvent.accommodationId
        });
        
        // Verificar se o accommodationId foi perdido e corrigir se necessário
        if (eventAfterUpdate && field !== 'accommodationId' && currentEvent.accommodationId && !eventAfterUpdate.accommodationId) {
          console.warn('[UnifiedItemsView] O accommodationId foi perdido durante a atualização. Restaurando...');
          
          // Restaurar o accommodationId original
          const fixedEvents = [...eventsAfterUpdate];
          const fixedEventIndex = fixedEvents.findIndex(e => e.id === accommodationEventId);
          
          if (fixedEventIndex >= 0) {
            fixedEvents[fixedEventIndex] = {
              ...fixedEvents[fixedEventIndex],
              accommodationId: currentEvent.accommodationId
            };
            
            // Aplicar a correção
            updatePage(page.id, 'accommodationEvents', fixedEvents);
            
            console.log('[UnifiedItemsView] AccommodationId restaurado:', currentEvent.accommodationId);
          }
        }
      }, 10);
    };

    const deleteAccommodationEvent = (pageId: string, accommodationEventId: string) => {
      console.log('[UnifiedItemsView] Removendo evento de hospedagem:', accommodationEventId);
      removeItem(accommodationEventId, 'accommodation-event');
    };

    // Verificar se há páginas disponíveis para mover
    const hasAvailablePages = allPages && allPages.length > 1 && allPages.some(p => p.id !== page.id);
    
    console.log('[UnifiedItemsView] Verificação de disponibilidade para evento de hospedagem:', {
      hasAvailablePages,
      allPagesLength: allPages?.length,
      currentPageId: page.id,
      hasMoveAccommodationEventToPage: Boolean(moveAccommodationEventToPage),
      allAccommodationsCount: allAccommodations.length
    });
    
    // Simplificar a lógica - apenas verificar se existem páginas disponíveis para mover
    // A verificação da função moveAccommodationEventToPage será feita no momento do clique
    const handleAccommodationEventMove = hasAvailablePages ? 
      (accommodationEventId: string) => {
        console.log("[UnifiedItemsView] Iniciando movimentação de evento de hospedagem", accommodationEventId);
        
        // Abrir o modal de seleção de página
        setSelectionType('accommodationEvent');
        setSelectedAccommodationEventId(accommodationEventId);
        setIsPageSelectionOpen(true);
      } 
      : undefined;

    // Log para verificar a lista allAccommodations passada
    console.log('[UnifiedItemsView] Passando para AccommodationEventForm:', {
      accommodationEventId: accommodationEvent.id,
      totalAllAccommodations: allAccommodations.length,
      accommodationId: accommodationEvent.accommodationId,
      // Exemplo das primeiras 3 hospedagens para verificar
      sampleAccommodations: allAccommodations.slice(0, 3).map(a => ({ id: a.id, name: a.name, pageName: a.pageName }))
    });

    return (
      <AccommodationEventForm
        accommodationEvent={accommodationEvent}
        index={index}
        pageId={page.id}
        accommodations={allAccommodations}
        updateAccommodationEvent={updateAccommodationEvent}
        deleteAccommodationEvent={deleteAccommodationEvent}
        moveAccommodationEvent={handleAccommodationEventMove}
        isCollapsed={activeItemId !== accommodationEvent.id}
        onToggleCollapse={() => toggleItem(accommodationEvent.id)}
      />
    );
  };

  // Get color and icon for each item type
  const getItemStyles = (item: UnifiedItem) => {
    if (item.type === 'block') {
      return {
        borderColor: "#e6a23c",
        backgroundColor: "#fbf0e2",
        icon: <Box className="h-4 w-4 text-[#e6a23c]" />
      };
    } else if (item.type === 'flight') {
      return {
        borderColor: "#3182ce",
        backgroundColor: "#ebf8ff",
        icon: <Plane className="h-4 w-4 text-blue-500" />
      };
    } else if (item.type === 'accommodation') {
      return {
        borderColor: "#38a169",
        backgroundColor: "#f0fff4",
        icon: <Building className="h-4 w-4 text-green-500" />
      };
    } else if (item.type === 'accommodation-checkin') {
      return {
        borderColor: "#16a34a", // Verde mais escuro para check-in
        backgroundColor: "#dcfce7",
        icon: <LogIn className="h-4 w-4 text-green-600" />
      };
    } else if (item.type === 'accommodation-checkout') {
      return {
        borderColor: "#ef4444", // Vermelho para check-out
        backgroundColor: "#fee2e2",
        icon: <LogOut className="h-4 w-4 text-red-600" />
      };
    } else if (item.type === 'tour') {
      return {
        borderColor: "#d69e2e",
        backgroundColor: "#fffbeb",
        icon: <Map className="h-4 w-4 text-amber-500" />
      };
    } else if (item.type === 'transportation') {
      return {
        borderColor: "#2b6cb0",
        backgroundColor: "#e6f6ff",
        icon: <Car className="h-4 w-4 text-blue-600" />
      };
    } else if (item.type === 'restaurant') {
      return {
        borderColor: "#805ad5",
        backgroundColor: "#f5f0ff",
        icon: <Utensils className="h-4 w-4 text-purple-600" />
      };
    } else if (item.type === 'accommodation-event' && item.accommodationEvent) {
      if (item.accommodationEvent.isCheckIn) {
        return {
          borderColor: "#16a34a",
          backgroundColor: "#dcfce7",
          icon: <LogIn className="h-4 w-4 text-green-600" />
        };
      } else {
        return {
          borderColor: "#ef4444",
          backgroundColor: "#fee2e2",
          icon: <LogOut className="h-4 w-4 text-red-600" />
        };
      }
    }
    
    return {
      borderColor: "#718096",
      backgroundColor: "#f7fafc",
      icon: <CircleDot className="h-4 w-4 text-gray-500" />
    };
  };

  // Function to handle moving an item to another page
  const handleMoveToPage = (targetPageId: string) => {
    console.log('[UnifiedItemsView] handleMoveToPage chamado:', {
      targetPageId,
      selectionType,
      selectedFlightEventId,
      selectedAccommodationEventId,
      hasMoveFlightEventFunction: Boolean(moveFlightEventToPage),
      hasMoveAccommodationEventFunction: Boolean(moveAccommodationEventToPage),
      hasMoveTransportationFunction: Boolean(moveTransportationToPage),
      hasMoveFlightToPageFunction: Boolean(moveFlightToPage),
      hasMoveBlockToPageFunction: Boolean(moveBlockToPage),
      hasFallbackFunction: true,
      hasGlobalFunctionFlightEvent: typeof globalMoveFlightEvent === 'function',
      hasGlobalFunctionAccommodationToPage: typeof globalMoveAccommodationToPage === 'function',
      hasGlobalFunctionTransportationToPage: typeof globalMoveTransportationToPage === 'function',
      hasGlobalFunctionFlightToPage: typeof globalMoveFlightToPageFunc === 'function'
    });
    
    if (selectionType === 'tour') {
      if (!selectedTourId || !moveTourToPage) return;
      
      // Call the moveTourToPage function with source page ID, tour ID and target page ID
      moveTourToPage(page.id, selectedTourId, targetPageId);
      
      // Show success notification
      toast({
        title: "Passeio movido",
        description: "O passeio foi movido para a página selecionada.",
      });
    } else if (selectionType === 'restaurant') {
      if (!selectedRestaurantId || !moveRestaurantToPage) return;
      
      // Call the moveRestaurantToPage function with source page ID, restaurant ID and target page ID
      moveRestaurantToPage(page.id, selectedRestaurantId, targetPageId);
      
      // Show success notification
      toast({
        title: "Restaurante movido",
        description: "O restaurante foi movido para a página selecionada.",
      });
    } else if (selectionType === 'accommodation') {
      if (!selectedAccommodationId) {
        console.log('[UnifiedItemsView] Não é possível mover a hospedagem - ID não selecionado');
        return;
      }

      try {
        // Vamos criar uma cópia local da hospedagem antes de movê-la (para debug)
        const accommodationToMove = page.accommodations?.find(a => a.id === selectedAccommodationId);
        if (accommodationToMove) {
          console.log('[UnifiedItemsView] Cópia local da hospedagem antes de mover:', JSON.stringify(accommodationToMove));
          // Guardar em localStorage para debug futuro
          localStorage.setItem('lastMovedAccommodation', JSON.stringify({
            accommodation: accommodationToMove,
            sourcePageId: page.id,
            targetPageId,
            timestamp: new Date().toISOString()
          }));
        }
        
        // Decidir qual função usar: prop, global ou local
        if (typeof moveAccommodationToPage === 'function') {
          console.log('[UnifiedItemsView] Usando função moveAccommodationToPage recebida como prop');
          moveAccommodationToPage(page.id, selectedAccommodationId, targetPageId);
          
          toast({
            title: "Hospedagem movida",
            description: "A hospedagem foi movida para a página selecionada.",
          });
        } else if (typeof globalMoveAccommodationToPage === 'function') {
          console.log('[UnifiedItemsView] Usando função globalMoveAccommodationToPage');
          globalMoveAccommodationToPage(page.id, selectedAccommodationId, targetPageId);
          
          toast({
            title: "Hospedagem movida",
            description: "A hospedagem foi movida para a página selecionada usando a função global.",
          });
        } else {
          console.log('[UnifiedItemsView] Nenhuma função de movimento disponível para hospedagem');
          toast({
            title: "Funcionalidade não implementada",
            description: "A função para mover hospedagens entre páginas não está disponível.",
            variant: "destructive"
          });
        }
      } catch (error) {
        console.error('[UnifiedItemsView] Erro ao mover hospedagem:', error);
        toast({
          title: "Erro ao mover hospedagem",
          description: "Ocorreu um erro ao tentar mover a hospedagem: " + (error instanceof Error ? error.message : String(error)),
          variant: "destructive"
        });
      }
    } else if (selectionType === 'flightEvent') {
      if (!selectedFlightEventId) {
        console.log('[UnifiedItemsView] Não é possível mover o evento de voo - ID não selecionado');
        return;
      }
      
      try {
        // Vamos criar uma cópia local do evento de voo antes de movê-lo (para debug)
        const flightEventToMove = page.flightEvents?.find(e => e.id === selectedFlightEventId);
        if (flightEventToMove) {
          console.log('[UnifiedItemsView] Cópia local do evento antes de mover:', JSON.stringify(flightEventToMove));
          // Guardar em localStorage para debug futuro
          localStorage.setItem('lastMovedFlightEvent', JSON.stringify({
            event: flightEventToMove,
            sourcePageId: page.id,
            targetPageId,
            timestamp: new Date().toISOString()
          }));
        }
        
        // Decidir qual função usar: prop, global ou local
        if (typeof moveFlightEventToPage === 'function') {
          console.log('[UnifiedItemsView] Usando função moveFlightEventToPage recebida como prop');
          moveFlightEventToPage(page.id, selectedFlightEventId, targetPageId);
        } else if (typeof globalMoveFlightEvent === 'function') {
          console.log('[UnifiedItemsView] Usando função globalMoveFlightEvent');
          globalMoveFlightEvent(page.id, selectedFlightEventId, targetPageId);
        } else {
          console.log('[UnifiedItemsView] Usando implementação local');
          localMoveFlightEventToPage(page.id, selectedFlightEventId, targetPageId);
        }
        
        console.log('[UnifiedItemsView] Movimento de evento concluído');
      } catch (error) {
        console.error('[UnifiedItemsView] Erro ao mover flight event:', error);
        toast({
          title: "Erro ao mover evento",
          description: "Ocorreu um erro ao tentar mover o evento de voo.",
          variant: "destructive"
        });
      }
    } else if (selectionType === 'accommodationEvent') {
      if (!selectedAccommodationEventId) {
        console.log('[UnifiedItemsView] Não é possível mover o evento de hospedagem - ID não selecionado');
        return;
      }
      
      try {
        // Vamos criar uma cópia local do evento de hospedagem antes de movê-lo (para debug)
        const accommodationEventToMove = page.accommodationEvents?.find(e => e.id === selectedAccommodationEventId);
        if (accommodationEventToMove) {
          console.log('[UnifiedItemsView] Cópia local do evento antes de mover:', JSON.stringify(accommodationEventToMove));
          // Guardar em localStorage para debug futuro
          localStorage.setItem('lastMovedAccommodationEvent', JSON.stringify({
            event: accommodationEventToMove,
            sourcePageId: page.id,
            targetPageId,
            timestamp: new Date().toISOString()
          }));
        }
        
        // Decidir qual função usar: prop ou local
        if (typeof moveAccommodationEventToPage === 'function') {
          console.log('[UnifiedItemsView] Usando função moveAccommodationEventToPage recebida como prop');
          moveAccommodationEventToPage(page.id, selectedAccommodationEventId, targetPageId);
        } else {
          console.log('[UnifiedItemsView] Usando implementação local');
          localMoveAccommodationEventToPage(page.id, selectedAccommodationEventId, targetPageId);
        }
        
        console.log('[UnifiedItemsView] Movimento de evento de hospedagem concluído');
      } catch (error) {
        console.error('[UnifiedItemsView] Erro ao mover accommodation event:', error);
        toast({
          title: "Erro ao mover evento",
          description: "Ocorreu um erro ao tentar mover o evento de hospedagem.",
          variant: "destructive"
        });
      }
    } else if (selectionType === 'transportation') {
      if (!selectedTransportationId) {
        console.log('[UnifiedItemsView] Cannot move transportation - ID not selected');
        return;
      }

      try {
        // Create a local copy of the transportation for debug
        const transportationToMove = page.transportations?.find(t => t.id === selectedTransportationId);
        if (transportationToMove) {
          console.log('[UnifiedItemsView] Local copy of transportation before moving:', JSON.stringify(transportationToMove));
          // Store in localStorage for future debug
          localStorage.setItem('lastMovedTransportation', JSON.stringify({
            transportation: transportationToMove,
            sourcePageId: page.id,
            targetPageId,
            timestamp: new Date().toISOString()
          }));
        }
        
        // Decide which function to use: prop or global
        if (typeof moveTransportationToPage === 'function') {
          console.log('[UnifiedItemsView] Using moveTransportationToPage prop function');
          moveTransportationToPage(page.id, selectedTransportationId, targetPageId);
          
          toast({
            title: "Traslado movido",
            description: "O traslado foi movido para a página selecionada.",
          });
        } else if (typeof globalMoveTransportationToPage === 'function') {
          console.log('[UnifiedItemsView] Using globalMoveTransportationToPage global function');
          globalMoveTransportationToPage(page.id, selectedTransportationId, targetPageId);
          
          toast({
            title: "Traslado movido",
            description: "O traslado foi movido para a página selecionada usando a função global.",
          });
        } else {
          console.log('[UnifiedItemsView] No movement function available for transportation');
          toast({
            title: "Funcionalidade não implementada",
            description: "A função para mover traslados entre páginas não está disponível.",
            variant: "destructive"
          });
        }
      } catch (error) {
        console.error('[UnifiedItemsView] Error moving transportation:', error);
        toast({
          title: "Erro ao mover traslado",
          description: "Ocorreu um erro ao tentar mover o traslado: " + (error instanceof Error ? error.message : String(error)),
          variant: "destructive"
        });
      }
    } else if (selectionType === 'flight') {
      if (!selectedFlightId) {
        console.log('[UnifiedItemsView] Não é possível mover o voo - ID não selecionado');
        toast({
          title: "Erro ao mover voo",
          description: "Nenhum voo selecionado para mover.",
          variant: "destructive"
        });
        return;
      }

      try {
        // Criar uma cópia local do voo antes de movê-lo (para debug)
        const flightToMove = page.flights?.find(f => f.id === selectedFlightId);
        if (flightToMove) {
          console.log('[UnifiedItemsView] Cópia local do voo antes de mover:', JSON.stringify(flightToMove));
          // Guardar em localStorage para debug futuro
          localStorage.setItem('lastMovedFlight', JSON.stringify({
            flight: flightToMove,
            sourcePageId: page.id,
            targetPageId,
            timestamp: new Date().toISOString()
          }));
        }
        
        // Decidir qual função usar: prop ou global
        if (typeof moveFlightToPage === 'function') {
          console.log('[UnifiedItemsView] Usando função moveFlightToPage recebida como prop');
          moveFlightToPage(page.id, selectedFlightId, targetPageId);
          
          toast({
            title: "Voo movido",
            description: "O voo foi movido para a página selecionada.",
          });
        } else if (typeof globalMoveFlightToPageFunc === 'function') {
          console.log('[UnifiedItemsView] Usando função globalMoveFlightToPage global');
          globalMoveFlightToPageFunc(page.id, selectedFlightId, targetPageId);
          
          toast({
            title: "Voo movido",
            description: "O voo foi movido para a página selecionada usando a função global.",
          });
        } else {
          console.log('[UnifiedItemsView] Nenhuma função disponível para mover voo');
          toast({
            title: "Funcionalidade não disponível",
            description: "A função para mover voos não está disponível.",
            variant: "destructive"
          });
        }
      } catch (error) {
        console.error('[UnifiedItemsView] Erro ao mover voo:', error);
        toast({
          title: "Erro ao mover voo",
          description: `Ocorreu um erro: ${error instanceof Error ? error.message : String(error)}`,
          variant: "destructive"
        });
      }
    } else if (selectionType === 'block') {
      if (!selectedBlockId) {
        console.log('[UnifiedItemsView] Não é possível mover o bloco - ID não selecionado');
        toast({
          title: "Erro ao mover bloco",
          description: "Nenhum bloco selecionado para mover.",
          variant: "destructive"
        });
        return;
      }

      try {
        // Criar uma cópia local do bloco antes de movê-lo (para debug)
        const blockToMove = page.blocks?.find(b => b.id === selectedBlockId);
        if (blockToMove) {
          console.log('[UnifiedItemsView] Cópia local do bloco antes de mover:', JSON.stringify(blockToMove));
          // Guardar em localStorage para debug futuro
          localStorage.setItem('lastMovedBlock', JSON.stringify({
            block: blockToMove,
            sourcePageId: page.id,
            targetPageId,
            timestamp: new Date().toISOString()
          }));
        }
        
        // Decidir qual função usar: prop ou local
        if (typeof moveBlockToPage === 'function') {
          console.log('[UnifiedItemsView] Usando função moveBlockToPage recebida como prop');
          moveBlockToPage(page.id, selectedBlockId, targetPageId);
        } else {
          console.log('[UnifiedItemsView] Usando implementação local');
          localMoveBlockToPage(page.id, selectedBlockId, targetPageId);
        }
        
        console.log('[UnifiedItemsView] Movimento de bloco concluído');
      } catch (error) {
        console.error('[UnifiedItemsView] Erro ao mover bloco:', error);
        toast({
          title: "Erro ao mover bloco",
          description: "Ocorreu um erro ao tentar mover o bloco: " + (error instanceof Error ? error.message : String(error)),
          variant: "destructive"
        });
      }
    }
    
    // Reset state
    setSelectedTourId(null);
    setSelectedRestaurantId(null);
    setSelectedAccommodationId(null);
    setSelectedFlightEventId(null);
    setSelectedAccommodationEventId(null);
    setSelectedTransportationId(null);
    setSelectedFlightId(null);
    setSelectionType(null);
    setIsPageSelectionOpen(false);
  };

  // Add a new handler for accommodation movement:
  const handleInitiateAccommodationMove = (accommodationId: string) => {
    console.log('[UnifiedItemsView] handleInitiateAccommodationMove chamado:', {
      accommodationId,
      hasMoveFunction: typeof moveAccommodationToPage === 'function',
      hasGlobalFunction: typeof globalMoveAccommodationToPage === 'function',
      allPagesAvailable: Array.isArray(allPages),
      allPagesCount: allPages?.length,
      currentPageId: page.id
    });
    
    // Verifique se temos props necessárias e dados
    if (!moveAccommodationToPage && !globalMoveAccommodationToPage) {
      console.log('[UnifiedItemsView] Nenhuma função de movimento disponível');
      toast({
        title: "Operação não disponível",
        description: "A função para mover hospedagens não está disponível.",
      });
      return;
    }
    
    // Garantir que allPages é um array
    if (!Array.isArray(allPages) || allPages.length <= 1) {
      console.log('[UnifiedItemsView] Não há páginas suficientes:', allPages);
      toast({
        title: "Operação não disponível",
        description: "É necessário ter mais de uma página disponível para mover a hospedagem.",
      });
      return;
    }
    
    // Calcular páginas disponíveis (excluindo a página atual)
    const availablePages = allPages.filter(p => p.id !== page.id);
    console.log('[UnifiedItemsView] Páginas disponíveis:', {
      count: availablePages.length,
      pageIds: availablePages.map(p => p.id),
      excludedPageId: page.id
    });
    
    if (availablePages.length === 0) {
      console.log('[UnifiedItemsView] Nenhuma página disponível após filtragem');
      toast({
        title: "Operação não disponível",
        description: "É necessário ter mais de uma página disponível para mover a hospedagem.",
      });
      return;
    }
    
    // Abrir o modal de seleção de página
    console.log('[UnifiedItemsView] Abrindo modal de seleção de página para hospedagem:', accommodationId);
    setSelectionType('accommodation');
    setSelectedAccommodationId(accommodationId);
    setIsPageSelectionOpen(true);
  };

  // Adicionar novo componente para o menu de contexto de transportes
  const TransportationContextMenu: React.FC<TransportationContextMenuProps> = ({ pageId, transportationId, allPages }) => {
    const [isPageSelectionModalOpen, setIsPageSelectionModalOpen] = useState(false);
    const { toast } = useToast();

    const handleMoveToPage = () => {
      console.log("[TransportationContextMenu] Opening page selection modal", { pageId, transportationId });
      setIsPageSelectionModalOpen(true);
    };

    const handlePageSelected = (targetPageId: string) => {
      try {
        console.log("[TransportationContextMenu] Attempting to move transportation", {
          sourcePageId: pageId,
          transportationId,
          targetPageId
        });
        
        // Verificar se temos páginas e se a página de destino existe
        if (!Array.isArray(allPages) || allPages.length === 0) {
          console.error("[TransportationContextMenu] No pages available", { allPages });
          toast({
            title: "Erro ao mover transporte",
            description: "Não há páginas disponíveis para mover o transporte",
            variant: "destructive"
          });
          return;
        }
        
        const targetPageExists = allPages.some(page => page.id === targetPageId);
        if (!targetPageExists) {
          console.error("[TransportationContextMenu] Target page does not exist", { targetPageId, allPages });
          toast({
            title: "Página inválida",
            description: "A página de destino selecionada não existe",
            variant: "destructive"
          });
          return;
        }
        
        // Chamar a função global para mover o transporte
        const result = globalMoveTransportationToPage(pageId, transportationId, targetPageId);
        
        if (result) {
          toast({
            title: "Transporte movido",
            description: "O transporte foi movido com sucesso para a nova página",
          });
        } else {
          toast({
            title: "Erro ao mover transporte",
            description: "Não foi possível mover o transporte para a página selecionada",
            variant: "destructive"
          });
        }
      } catch (error) {
        console.error("[TransportationContextMenu] Error moving transportation:", error);
        toast({
          title: "Erro ao mover transporte",
          description: "Ocorreu um erro ao tentar mover o transporte",
          variant: "destructive"
        });
      } finally {
        setIsPageSelectionModalOpen(false);
      }
    };

    return (
      <>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleMoveToPage} className="cursor-pointer">
              <MoveRight className="mr-2 h-4 w-4" />
              <span>Mover para outra página</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {isPageSelectionModalOpen && (
          <PageSelectionModal
            isOpen={isPageSelectionModalOpen}
            onClose={() => setIsPageSelectionModalOpen(false)}
            pages={allPages}
            onSelectPage={handlePageSelected}
            currentPageId={pageId}
            title="Selecionar página de destino para transporte"
          />
        )}
      </>
    );
  };

  // Função para iniciar movimento de voo
  const handleInitiateFlightMove = (flightId: string) => {
    console.log('[UnifiedItemsView] handleInitiateFlightMove chamado:', {
      flightId,
      hasMoveFunction: typeof moveFlightToPage === 'function',
      hasGlobalMoveFunction: typeof globalMoveFlightToPageFunc === 'function',
      pageId: page.id
    });

    // Verificar se temos as funções e dados necessários
    if (!moveFlightToPage && !globalMoveFlightToPageFunc) {
      console.log('[UnifiedItemsView] Nenhuma função de movimentação disponível');
      toast({
        title: "Operação não disponível",
        description: "A função para mover voos não está disponível.",
      });
      return;
    }

    // Calcular páginas disponíveis (excluindo a página atual)
    const availablePages = Array.isArray(allPages) && allPages.length > 1
      ? allPages.filter(p => p.id !== page.id)
      : [];

    if (availablePages.length === 0) {
      toast({
        title: "Operação não disponível",
        description: "É necessário ter mais de uma página disponível para mover o voo.",
      });
      return;
    }

    // Definir o tipo de seleção e o ID do voo e abrir o modal
    setSelectionType('flight');
    setSelectedFlightId(flightId);
    setIsPageSelectionOpen(true);
  };

  // No início da função UnifiedItemsView, adicionar o estado selectedBlockId
  const [selectedBlockId, setSelectedBlockId] = useState<string>('');

  // Perto do final da função, antes do return, adicionar a implementação de localMoveBlockToPage
  const localMoveBlockToPage = (sourcePageId: string, blockId: string, targetPageId: string) => {
    console.log('[UnifiedItemsView] localMoveBlockToPage chamado:', {
      sourcePageId,
      blockId,
      targetPageId
    });
    
    try {
      // Encontrar o bloco na página de origem
      const blockToMove = page.blocks?.find(b => b.id === blockId);
      
      if (!blockToMove) {
        console.error('[UnifiedItemsView] Bloco não encontrado:', blockId);
        toast({
          title: "Erro ao mover bloco",
          description: "Bloco não encontrado na página de origem",
          variant: "destructive"
        });
        return;
      }
      
      // Criar cópia do bloco
      const blockCopy = JSON.parse(JSON.stringify(blockToMove));
      
      // Remover bloco da página atual
      const updatedBlocksSource = page.blocks.filter(b => b.id !== blockId);
      updatePage(page.id, 'blocks', updatedBlocksSource);
      
      // Buscar a página de destino entre todas as páginas
      const targetPage = allPages.find(p => p.id === targetPageId);
      
      if (!targetPage) {
        console.error('[UnifiedItemsView] Página de destino não encontrada:', targetPageId);
        toast({
          title: "Erro ao mover bloco",
          description: "Página de destino não encontrada",
          variant: "destructive"
        });
        
        // Tentar restaurar o bloco na página original
        updatePage(page.id, 'blocks', [...updatedBlocksSource, blockCopy]);
        return;
      }
      
      // Adicionar bloco à página de destino
      const targetPageBlocks = [...(targetPage.blocks || []), blockCopy];
      updatePage(targetPageId, 'blocks', targetPageBlocks);
      
      toast({
        title: "Bloco movido",
        description: `O bloco foi movido com sucesso para ${targetPage.title}`,
      });
      
      console.log('[UnifiedItemsView] Bloco movido com sucesso', {
        sourceBlocks: updatedBlocksSource.length,
        targetBlocks: targetPageBlocks.length
      });
    } catch (error) {
      console.error('[UnifiedItemsView] Erro ao mover bloco:', error);
      toast({
        title: "Erro ao mover bloco",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive"
      });
    }
  };

  // Adicionar uma função para iniciar o movimento de bloco na UnifiedItemsView
  const handleInitiateBlockMove = (blockId: string) => {
    console.log('[UnifiedItemsView] Iniciando movimento de bloco', {
      blockId: blockId,
      pageId: page.id
    });
    
    if (!blockId) {
      console.error('[UnifiedItemsView] Erro: ID do bloco não definido');
      toast({
        title: "Erro ao mover bloco",
        description: "ID do bloco não definido",
        variant: "destructive"
      });
      return;
    }

    // Verificar se temos allPages e se é um array com pelo menos uma página
    if (!Array.isArray(allPages) || allPages.length <= 1) {
      console.log('[UnifiedItemsView] Movimento não disponível - não há outras páginas', {
        allPagesIsArray: Array.isArray(allPages),
        allPagesLength: Array.isArray(allPages) ? allPages.length : 'não é array'
      });
      toast({
        title: "Movimento não disponível",
        description: "Não existem outras páginas para mover este bloco",
        variant: "destructive"
      });
      return;
    }

    // Verificar se temos a função moveBlockToPage ou o fallback global
    if (typeof moveBlockToPage !== 'function') {
      console.log('[UnifiedItemsView] Função moveBlockToPage não disponível, usando implementação local');
    }

    // Filtrar a página atual das opções
    const availablePages = allPages.filter(p => p.id !== page.id);
    
    if (availablePages.length === 0) {
      toast({
        title: "Movimento não disponível",
        description: "Não existem outras páginas para mover este bloco",
        variant: "destructive"
      });
      return;
    }

    // Armazenar o ID do bloco e definir o tipo de seleção
    setSelectedBlockId(blockId);
    setSelectionType('block');
    setIsPageSelectionOpen(true);
  };

  // Log quando a página de cotação é renderizada
  console.log('[UnifiedItemsView] Rendering page:', {
    pageId: page.id,
    pageTitle: page.title,
    totalBlocks: page.blocks?.length || 0,
    totalFlights: page.flights?.length || 0,
    totalAccommodations: page.accommodations?.length || 0,
    totalTours: page.tours?.length || 0,
    totalTransportations: page.transportations?.length || 0,
    totalRestaurants: page.restaurants?.length || 0,
    totalFlightEvents: page.flightEvents?.length || 0,
    totalAccommodationEvents: page.accommodationEvents?.length || 0,
    allPagesAvailable: Boolean(allPages),
    totalAllAccommodations: allAccommodations.length,
    totalAllFlights: allFlights.length
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-medium">Itens da Cotação</h3>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            className="w-8 h-8 p-0 text-amber-600 border-amber-600 hover:bg-amber-50"
            onClick={() => handleOpenLibrary()}
            title="Abrir Biblioteca"
          >
            <BookOpen className="h-4 w-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="default"
                size="sm"
                className="w-8 h-8 p-0 bg-[#087a83] hover:bg-[#076570] text-white"
                title="Adicionar Item"
              >
                <Plus className="h-4 w-4 text-white" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {addBlock && (
                <DropdownMenuItem onClick={() => addBlock(page.id)} className="cursor-pointer">
                  <Box className="h-4 w-4 mr-2 text-[#e6a23c]" />
                  <span>Bloco</span>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => addFlight(page.id)} className="cursor-pointer">
                <Plane className="h-4 w-4 mr-2 text-blue-500" />
                <span>Voo</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => addAccommodation(page.id)} className="cursor-pointer">
                <Building className="h-4 w-4 mr-2 text-green-500" />
                <span>Hospedagem</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => addTour(page.id)} className="cursor-pointer">
                <Map className="h-4 w-4 mr-2 text-amber-500" />
                <span>Passeio</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => addTransportation(page.id)} className="cursor-pointer">
                <Car className="h-4 w-4 mr-2 text-blue-500" />
                <span>Traslado</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => addRestaurant(page.id)} className="cursor-pointer">
                <Utensils className="h-4 w-4 mr-2 text-purple-500" />
                <span>Restaurante</span>
              </DropdownMenuItem>
              <DropdownMenuItem className="border-t mt-1">
                <span className="text-gray-500 text-sm">Eventos de Voo</span>
              </DropdownMenuItem>
              {addFlightEvent && (
                <DropdownMenuItem onClick={() => addFlightEvent(page.id)} className="cursor-pointer">
                  <Plane className="h-4 w-4 mr-2 text-blue-500" />
                  <span>Saída / Chegada</span>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem className="border-t mt-1 pt-1">
                <span className="text-gray-500 text-sm">Eventos de Hospedagem</span>
              </DropdownMenuItem>
              {addAccommodationEvent && (
                <DropdownMenuItem onClick={() => addAccommodationEvent(page.id)} className="cursor-pointer">
                  <Building className="h-4 w-4 mr-2 text-green-500" />
                  <span>Check-in / Check-out</span>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="unified-items">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="space-y-3"
            >
              {items.length === 0 ? (
                <div className="text-center py-6 border border-dashed rounded-md">
                  <p className="text-gray-500">Nenhum item adicionado ainda.</p>
                </div>
              ) : (
                items.map((item, index) => {
                  const { icon } = getItemStyles(item);
                  
                  return (
                  <Draggable 
                    key={item.id} 
                    draggableId={item.id} 
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={cn(
                          "mb-3 rounded transition-shadow",
                          snapshot.isDragging && "shadow-lg bg-gray-50"
                        )}
                        style={{
                          ...provided.draggableProps.style,
                        }}
                      >
                        <div className="flex items-start">
                          <DragHandle dragHandleProps={provided.dragHandleProps} />
                          <div className="flex-grow overflow-hidden">
                            {renderItem(item)}
                          </div>
                        </div>
                      </div>
                    )}
                  </Draggable>
                  );
                })
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Library Browser Modal */}
      <LibraryBrowser
        isOpen={isLibraryOpen}
        onClose={() => setIsLibraryOpen(false)}
        onSelectItem={handleSelectLibraryItem}
        itemType={currentItemType}
      />

      {/* Flight Modal */}
      {selectedFlight && (
        <Dialog open={isFlightModalOpen} onOpenChange={setIsFlightModalOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedFlight.airline} {selectedFlight.flightNumber}
              </DialogTitle>
              <DialogDescription>
                {selectedFlight.departureAirport || 'Partida'} → {selectedFlight.arrivalAirport || 'Chegada'}
              </DialogDescription>
            </DialogHeader>
            {/* Flight details */}
            <div className="p-4 bg-white rounded-md shadow space-y-4">
              {/* Partida e chegada */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium mb-1">Partida</h3>
                  <p className="text-gray-600 text-sm">{selectedFlight.departureAirport}</p>
                  {selectedFlight.departureDateTime && (
                    <p className="text-gray-600 text-sm">
                      {new Date(selectedFlight.departureDateTime).toLocaleString()}
                    </p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-medium mb-1">Chegada</h3>
                  <p className="text-gray-600 text-sm">{selectedFlight.arrivalAirport}</p>
                  {selectedFlight.arrivalDateTime && (
                    <p className="text-gray-600 text-sm">
                      {new Date(selectedFlight.arrivalDateTime).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Page Selection Modal for moving tours */}
      {isPageSelectionOpen && (
        <PageSelectionModal
          isOpen={isPageSelectionOpen}
          onClose={() => {
            console.log('[UnifiedItemsView] Fechando modal de seleção de página');
            setIsPageSelectionOpen(false);
            setSelectedTourId(null);
            setSelectedRestaurantId(null);
            setSelectedAccommodationId(null);
            setSelectedFlightEventId(null);
            setSelectedAccommodationEventId(null);
            setSelectedTransportationId(null);
            setSelectedFlightId(null);
            setSelectedBlockId(null);
          }}
          onPageSelect={handleMoveToPage}
          pages={allPages.filter(p => p.id !== page.id)}
          title="Selecione uma página para mover o item"
        />
      )}
    </div>
  );
} 